import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BAR_CHART_HEIGHT_PX, Y_AXIS_WIDTH_PX } from './categoryAnalysisMeta';

// Descending widths shape the placeholder like a real sorted result.
const PLACEHOLDER_WIDTHS = ['95%', '70%', '55%', '40%', '25%'];

export const CategoryAnalysisSkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.categoryAnalysis.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="category-analysis-widget-title"
            {...getNodeTestIdProp('category-analysis-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('category-analysis-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('category-analysis-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('category-analysis-widget-title')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'category-analysis-icon-skeleton',
                            )}
                        />
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'category-analysis-widget-subtitle',
                        )}
                    >
                        <Skeleton
                            className="h-3 w-40 rounded-md"
                            {...getNodeTestIdProp(
                                'category-analysis-widget-subtitle-skeleton',
                            )}
                        />
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content>
                    <ul
                        className="flex flex-col justify-between gap-2"
                        style={{ height: BAR_CHART_HEIGHT_PX }}
                        {...getNodeTestIdProp(
                            'category-analysis-chart-skeleton',
                        )}
                    >
                        {PLACEHOLDER_WIDTHS.map((width, index) => (
                            <li
                                key={index}
                                className="flex items-center gap-3"
                                {...getNodeTestIdProp(
                                    `category-analysis-bar-placeholder-${index}`,
                                )}
                            >
                                <Skeleton
                                    className="h-3 shrink-0 rounded-md"
                                    style={{ width: Y_AXIS_WIDTH_PX - 16 }}
                                    {...getNodeTestIdProp(
                                        `category-analysis-bar-placeholder-label-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="h-6 rounded-md"
                                    style={{ width }}
                                    {...getNodeTestIdProp(
                                        `category-analysis-bar-placeholder-fill-${index}`,
                                    )}
                                />
                            </li>
                        ))}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
